import { useAppDispatch, useAppSelector } from "@/store"
import React, { useState, useEffect, useRef } from "react"
import { Box, Button, CloseButton, Flex, Input, Text } from "@chakra-ui/react"

import {
  selectAutoLoginPending,
  selectLoginPwd,
  selectPhoneVal,
  selectSetPhoneInfo,
  selectSetPhoneOrVerifyCode,
  SetPhoneOrVerifyCode,
  setAutoLoginPending,
  setPhoneVal,
  setSetPhoneInfo,
} from "@/stores/auth_slice"
import { useLoginAuto, useLoginNew, useLoginOld } from "./login_hook"
import {
  changeViewType,
  LoginApiVersion,
  LoginStatus,
  selectLoginApiVersion,
  selectLoginState,
  selectShowImgCodeInPhone,
  selectUsernameInput,
  setImgCodeObj,
  setLoginApiVersion,
  setShouldShowProcessing,
  updateLoginStatus,
  ViewType,
} from "@/stores/login_slice"
import { UserUsecase } from "@/usecases/user"
import { BizError } from "@/models/biz_model"
import { validateChinesePhone } from "@/utils/tools"

const SetPhoneVerifyView: React.FC = () => {
  const dispatch = useAppDispatch()
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false)
  const [verifyCodeVal, setVerifyCodeVal] = useState<string>("")
  const [canClose, setCanClose] = useState(false)

  const { msg } = useAppSelector(selectLoginState)
  const loginApiVersion = useAppSelector(selectLoginApiVersion)

  const loginPwd = useAppSelector(selectLoginPwd) // 登录密码
  const setPhoneOrVerifyCode = useAppSelector(selectSetPhoneOrVerifyCode)
  const { phoneTail, smsToken, countDown } = useAppSelector(selectSetPhoneInfo)
  const phoneVal = useAppSelector(selectPhoneVal)
  const autoLoginPending = useAppSelector(selectAutoLoginPending)

  const loginNew = useLoginNew()
  const loginOld = useLoginOld()
  const loginAuto = useLoginAuto()
  const setErrMsg = (msg: string) => {
    dispatch(updateLoginStatus({ msg, status: LoginStatus.error }))
  }

  const userNameInput = useAppSelector(selectUsernameInput)

  // 图片验证码 start ---
  const imgCode = useRef<string | null>(null)
  const [picPath, setPicPath] = useState("")
  const [picId, setPicId] = useState("")
  const showImgCodeInPhone = useAppSelector(selectShowImgCodeInPhone)

  const getImgCodeHandle = async () => {
    const getImgCodeRes = await UserUsecase.getImgCode()
    if (getImgCodeRes instanceof BizError) {
      setErrMsg(getImgCodeRes.displayErrorMsg)
      return
    }
    const { picPath, captchaId } = getImgCodeRes.unwrap()

    setPicPath(picPath)
    setPicId(captchaId)
  }
  useEffect(() => {
    getImgCodeHandle()
  }, [])
  // 图片验证码 end ---

  useEffect(() => {
    const timer = window.setTimeout(() => setCanClose(true), 60 * 1000)
    return () => window.clearTimeout(timer)
  }, [])

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countDown > 0) {
      timer = setTimeout(() => {
        dispatch(
          setSetPhoneInfo({
            phoneTail,
            smsToken,
            countDown: countDown - 1,
          })
        )
      }, 1000)
    }
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [countDown])

  // 发送验证码 按钮
  const sendVerifyCode = async () => {
    if (phoneVal === null || phoneVal.length === 0) {
      setErrMsg("请输入手机号")
      return
    }

    // 手机号格式验证
    const isValidate = validateChinesePhone(phoneVal)
    if (!isValidate) {
      setErrMsg("请输入正确的手机号")
      return
    }
    setErrMsg("")
    setIsSendingCode(true)

    try {
      if (autoLoginPending) {
        await loginAuto({ phone: phoneVal })
      } else {
        await loginNew({ pwd: loginPwd, phone: phoneVal })
      }
    } catch (error) {
      // Log the error for diagnostics and show a user-friendly message
      console.error("sendVerifyCode error:", error)
      setErrMsg("发送验证码失败，请重试")
    } finally {
      setIsSendingCode(false)
    }
  }

  // 重发验证码 按钮（仅 inputVerifyCode 模式）
  const resendVerifyCode = async () => {
    setErrMsg("")
    setIsSendingCode(true)

    try {
      if (autoLoginPending) {
        await loginAuto({})
      } else if (loginApiVersion === LoginApiVersion.oldApi) {
        await loginOld({ pwd: loginPwd })
      } else {
        await loginNew({ pwd: loginPwd, userName: userNameInput })
      }
    } catch (error) {
      console.error("resendVerifyCode error:", error)
      setErrMsg("发送验证码失败，请重试")
    } finally {
      setIsSendingCode(false)
    }
  }

  // 验证按钮
  const handleVerify = async () => {
    // 图片验证码(输错5次密码) + 手机验证码（企业登录）
    if (showImgCodeInPhone) {
      // 1、图片验证码
      const imgCodeStr = imgCode.current
      if (imgCodeStr === null || imgCodeStr.length === 0) {
        setErrMsg("请输入图片验证码")
        return
      }
      // 2、手机验证码
      if (verifyCodeVal === null || verifyCodeVal.length === 0) {
        setErrMsg("请输入短信验证码")
        return
      }

      if (picId && imgCodeStr) {
        // 一、登录，连接云桌面
        if (loginApiVersion === LoginApiVersion.newApi) {
          try {
            const isSuccess = autoLoginPending
              ? await loginAuto({
                  phone: phoneVal,
                  smsCode: verifyCodeVal,
                  smsToken,
                })
              : await loginNew({
                  captcha: imgCodeStr,
                  captchaId: picId,
                  pwd: loginPwd,
                  phone: phoneVal,
                  smsCode: verifyCodeVal,
                  smsToken,
                })
            if (isSuccess) {
              dispatch(setImgCodeObj({ captchaId: picId, captcha: imgCodeStr }))
              dispatch(changeViewType(ViewType.login))
              dispatch(setLoginApiVersion(LoginApiVersion.newApi))
            }
          } catch (error) {
            console.error("Phone verification failed:", error)
          }
        }

        // 二、重启/关机
        if (loginApiVersion === LoginApiVersion.oldApi) {
          try {
            const isSuccess = autoLoginPending
              ? await loginAuto({
                  phone: phoneVal,
                  smsCode: verifyCodeVal,
                  smsToken,
                })
              : await loginOld({
                  captcha: imgCodeStr,
                  captchaId: picId,
                  pwd: loginPwd,
                  phone: phoneVal,
                  smsCode: verifyCodeVal,
                  smsToken,
                })
            if (isSuccess) {
              // KS用户手机验证成功后，设置需要显示处理状态的标记，然后跳转回密码验证页面
              dispatch(setShouldShowProcessing(true))
              dispatch(changeViewType(ViewType.stopResumeVerify))
            }
          } catch (error) {
            console.error("Phone verification failed:", error)
          }
        }
      }
    } else {
      // 手机验证码（企业登录）
      // 1、手机验证码
      if (verifyCodeVal === null || verifyCodeVal.length === 0) {
        setErrMsg("请输入短信验证码")
        return
      }
      // 一、登录，连接云桌面
      if (loginApiVersion === LoginApiVersion.newApi) {
        try {
          const isSuccess = autoLoginPending
            ? await loginAuto({
                phone: phoneVal,
                smsCode: verifyCodeVal,
                smsToken,
              })
            : await loginNew({
                pwd: loginPwd,
                phone: phoneVal,
                smsCode: verifyCodeVal,
                smsToken,
                userName: userNameInput,
              })
          if (isSuccess) {
            dispatch(changeViewType(ViewType.login))
            dispatch(setLoginApiVersion(LoginApiVersion.newApi))
          }
        } catch (error) {
          console.error("Phone verification failed:", error)
        }
      }

      // 二、重启/关机
      if (loginApiVersion === LoginApiVersion.oldApi) {
        try {
          const isSuccess = autoLoginPending
            ? await loginAuto({
                phone: phoneVal,
                smsCode: verifyCodeVal,
                smsToken,
              })
            : await loginOld({
                pwd: loginPwd,
                phone: phoneVal,
                smsCode: verifyCodeVal,
                smsToken,
              })
          if (isSuccess) {
            // KS用户手机验证成功后，设置需要显示处理状态的标记，然后跳转回密码验证页面
            dispatch(setShouldShowProcessing(true))
            dispatch(changeViewType(ViewType.stopResumeVerify))
          }
        } catch (error) {
          console.error("Phone verification failed:", error)
        }
      }
    }
  }

  const setPhoneHandle = (value: string) => {
    validateChinesePhone(value) // 手机号格式验证
    dispatch(setPhoneVal(value))
  }

  const setVerifyCode = (value: string) => {
    setVerifyCodeVal(value)
  }

  const closeDialog = () => {
    dispatch(changeViewType(ViewType.login))
    dispatch(setLoginApiVersion(LoginApiVersion.newApi))
    // 验证码窗口内的提示只在该窗口展示，返回登录页时不再触发错误 toast。
    dispatch(updateLoginStatus({ msg: "", status: LoginStatus.idle }))
    // 关闭验证码窗口时清理自动登录 pending 状态，避免脏状态残留影响后续密码登录
    dispatch(setAutoLoginPending(false))
  }

  return (
    <Flex
      width="100vw"
      height="100vh"
      direction="column"
      align="center"
      justify="center"
    >
      <Flex
        position="relative"
        direction="column"
        align="center"
        padding="16px"
        width="470px"
        background="#30313c"
        borderRadius="10px"
      >
        {canClose ? (
          <CloseButton
            aria-label="关闭验证码窗口"
            position="absolute"
            top="8px"
            right="8px"
            color="rgba(255, 255, 255, 0.7)"
            _hover={{ backgroundColor: "rgba(255, 255, 255, 0.12)" }}
            onClick={closeDialog}
          />
        ) : null}
        {setPhoneOrVerifyCode === SetPhoneOrVerifyCode.setPhone && (
          <Text color="white" fontSize="16px" marginBottom="20px">
            请完善您的手机号信息
          </Text>
        )}
        {/* 图片验证码： 
        只有在输错5次密码，并且出现-10017（图片验证码不能为空）时，
        需要在手机验证码页面，显示图片验证码 */}
        {showImgCodeInPhone && (
          <>
            <Flex width="100%" height="58px">
              <Box
                flex={1}
                backgroundColor={"white"}
                borderRadius={"6px"}
                marginLeft={"14px"}
                height={"70px"}
              >
                {picPath ? (
                  <img src={picPath} alt="" />
                ) : (
                  <Text textAlign={"center"} lineHeight={"53px"}>
                    暂无验证码
                  </Text>
                )}
              </Box>

              <Button
                flex={1}
                height={"70px"}
                variant="ghost"
                borderRadius={0}
                color="rgba(255, 255, 255, 0.7)"
                colorScheme="whiteAlpha"
                marginRight="28px"
                onClick={() => {
                  getImgCodeHandle()
                }}
              >
                看不清，换一张
              </Button>
            </Flex>
            <Input
              marginTop={"22px"}
              width={"100%"}
              type="text"
              background="rgb(37, 38, 46)"
              color="white"
              variant="unstyled"
              paddingX="12px"
              paddingY="8px"
              _placeholder={{ color: "#999999;" }}
              fontSize="15px"
              borderRadius="8px"
              placeholder="请输入图片验证码"
              onChange={(e) => {
                imgCode.current = e.target.value.trim()
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleVerify()
                }
              }}
            />
          </>
        )}

        {/* 手机号，发送验证码 */}
        {setPhoneOrVerifyCode === SetPhoneOrVerifyCode.setPhone && (
          <Flex direction="row" align="center" width="100%">
            <Input
              type="text"
              background="rgb(37, 38, 46)"
              color="white"
              variant="unstyled"
              paddingX="12px"
              paddingY="8px"
              _placeholder={{ color: "#999999;" }}
              fontSize="15px"
              borderRadius="8px"
              placeholder="请输入手机号"
              onChange={(e) => {
                setPhoneHandle(e.target.value.trim())
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  sendVerifyCode()
                }
              }}
            />
            <Button
              marginLeft="12px"
              width="90px"
              height="35px"
              variant="outline"
              border="1px solid rgba(255, 255, 255, 0.21)"
              color={countDown > 0 ? "#666666" : "#CCCCCC"}
              colorScheme="whiteAlpha"
              fontSize="12px"
              isDisabled={countDown > 0 || isSendingCode}
              isLoading={isSendingCode}
              onClick={() => {
                sendVerifyCode()
              }}
            >
              {countDown > 0 ? `${countDown}s` : "发送验证码"}
            </Button>
          </Flex>
        )}

        {phoneTail && (
          <Text
            color="white"
            fontSize="13px"
            marginTop="28px"
            height={"7px"}
            marginBottom="4px"
          >
            {`已往您尾号为：${phoneTail} 的手机号发送了验证码，请在下方输入短信验证码`}
          </Text>
        )}

        {/* 短信验证码 + 验证按钮 */}
        <Flex direction="row" align="center" width="100%" marginTop="16px">
          <Input
            type="text"
            background="rgb(37, 38, 46)"
            color="white"
            variant="unstyled"
            paddingX="12px"
            paddingY="8px"
            _placeholder={{ color: "#999999;" }}
            fontSize="15px"
            borderRadius="8px"
            placeholder="请输入短信验证码"
            onChange={(e) => {
              setVerifyCode(e.target.value.trim())
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleVerify()
              }
            }}
          />
          <Button
            marginLeft="12px"
            width="90px"
            height="35px"
            variant="outline"
            border="1px solid rgba(255, 255, 255, 0.21)"
            color="#CCCCCC"
            colorScheme="whiteAlpha"
            fontSize="12px"
            onClick={() => {
              handleVerify()
            }}
          >
            验证
          </Button>
          {setPhoneOrVerifyCode === SetPhoneOrVerifyCode.inputVerifyCode && (
            <Button
              marginLeft="12px"
              width="90px"
              height="35px"
              variant="outline"
              border="1px solid rgba(255, 255, 255, 0.21)"
              color={countDown > 0 ? "#666666" : "#CCCCCC"}
              colorScheme="whiteAlpha"
              fontSize="12px"
              isDisabled={countDown > 0 || isSendingCode}
              isLoading={isSendingCode}
              onClick={() => {
                resendVerifyCode()
              }}
            >
              {countDown > 0 ? `${countDown}s` : "重发验证码"}
            </Button>
          )}
        </Flex>

        <Text color="red" fontSize="13px" marginTop="14px" height={"14px"}>
          {msg}
        </Text>
      </Flex>
    </Flex>
  )
}
export default SetPhoneVerifyView
